import {
  Card,
  CardContent,
  CardHeader,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
  Typography,
} from "@altimateai/lego";
import { useQuery } from "@tanstack/react-query";
import { getOrgUsageTransactions, getOrgUsageTransactionsGroupedByUser } from "@/helpers/apis";
import { useState } from "react";
import { Spinner, Stack } from "@altimateai/lego";
import { TransactionGroupedByUser, AggregatedTask } from "@/pages/credits/types";
import dayjs from "dayjs";
import { DataTable } from "@/components/tables";
import { Paginate } from "@/components/paginate";
import { useDateFromSearchParams } from "@/helpers/useTimeHooks";
import { DateRange } from "@/components/dateRange";
import { CREDITS_DATE_PREFIX } from "../constants";
import { downloadOrgUsageCSV, downloadOrgUsageByUsersCSV } from "@/helpers/apis";
import { Button } from "@altimateai/lego";
import { DownloadIcon } from "@/assets/icons";
import { formatNumber } from "@/uiCore/components/formatters/format";

export const OrgCreditsUsageHistory = () => {
  const [tab, setTab] = useState<"task" | "users">("task");
  const { startDate, endDate, onUpdate } = useDateFromSearchParams(
    dayjs().subtract(30, "day").toDate(),
    dayjs().toDate(),
    CREDITS_DATE_PREFIX
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const { data: tasksTableData, isLoading: isLoadingTableData } = useQuery({
    queryKey: ["org-usage-transactions", startDate, endDate, page, pageSize],
    queryFn: () =>
      getOrgUsageTransactions({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        page: page + 1,
        size: pageSize,
      }),
    enabled: tab === "task",
  });

  const { data: transactionsGroupedByUsersTableData, isLoading: isLoadingGroupedByUserTableData } =
    useQuery({
      queryKey: ["org-usage-transactions-grouped-by-user", startDate, endDate, page, pageSize],
      queryFn: () =>
        getOrgUsageTransactionsGroupedByUser({
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          page: page + 1,
          size: pageSize,
        }),
      enabled: tab === "users",
    });

  const taskTableHeaders = [
    {
      label: "AI Task/Teammate",
      key: "task_name",
      uid: "task_name",
      id: 0,
      render: (item: AggregatedTask) => <span>{item.task_name}</span>,
    },
    {
      label: "Total Credits Consumed",
      key: "total_credits_consumed",
      uid: "total_credits_consumed",
      render: (item: AggregatedTask) => <span>{formatNumber(item.total_credits_consumed)}</span>,
      id: 1,
    },
    {
      label: "Users",
      key: "users",
      uid: "users",
      render: (item: AggregatedTask) => {
        const userEntries = Object.entries(item.users);
        const displayUsers = userEntries.slice(0, 3);
        const hasMore = userEntries.length > 3;

        const content = (
          <span className={hasMore ? "al-cursor-pointer" : ""}>
            {displayUsers.map(([email, credits]) => `${email} (${credits})`).join(", ")}
            {hasMore && <span className="al-text-brand"> [more]</span>}
          </span>
        );

        if (hasMore) {
          return (
            <Tooltip
              title={
                <div className="al-space-y-2 al-max-h-80 overflow-auto al-w-80 al-m-2">
                  {userEntries.map(([email, credits], index) => (
                    <Stack gap={"none"} direction={"column"} key={index} className="al-space-y-1">
                      <span className="al-font-regular al-text-gray-900 al-truncate">{email}</span>
                      <span className="al-text-gray-600 al-font-normal">
                        {formatNumber(credits)} credits
                      </span>
                    </Stack>
                  ))}
                </div>
              }
            >
              {content}
            </Tooltip>
          );
        }

        return content;
      },
      id: 2,
    },
    {
      label: "Last Used",
      key: "last_used_on",
      uid: "last_used_on",
      render: (item: AggregatedTask) => (
        <span>{dayjs(item.last_used_on).format("DD MMM YYYY, hh:mm A")}</span>
      ),
      id: 3,
    },
  ];

  const usersTableHeader = [
    {
      label: "User",
      key: "user",
      uid: "user",
      id: 0,
      render: (item: TransactionGroupedByUser) => {
        if (item.display_name) {
          return (
            <Stack direction={"column"} gap={"none"}>
              <span className={"al-font-semibold"}>{item.display_name}</span>
              <span className={"al-text-sm al-text-muted-foreground"}>{item.user_email}</span>
            </Stack>
          );
        } else {
          return <span>{item.user_email}</span>;
        }
      },
    },
    {
      label: "Consumed Credits",
      key: "credits_consumed",
      uid: "credits_consumed",
      render: (item: TransactionGroupedByUser) => (
        <span>{formatNumber(item.credits_consumed)}</span>
      ),
      id: 1,
    },
    {
      label: "AI Task/Teammate",
      key: "teammate",
      uid: "teammate",
      render: (item: TransactionGroupedByUser) => {
        const displayTeammates = item.ai_teammates.slice(0, 3);
        const hasMore = item.ai_teammates.length > 3;
        const hasMultiple = item.ai_teammates.length > 1;

        const content = (
          <span className={hasMultiple ? "al-cursor-pointer" : ""}>
            {displayTeammates.map(t => t.teammate).join(", ")}
            {hasMore && <span className="al-text-brand"> [more]</span>}
          </span>
        );

        if (hasMultiple) {
          return (
            <Tooltip
              title={
                <div className="al-space-y-5 al-max-h-80 overflow-auto al-w-80 al-m-2">
                  {item.ai_teammates.map((t, index) => {
                    const percentage =
                      item.credits_consumed > 0
                        ? (t.credits_consumed / item.credits_consumed) * 100
                        : 0;
                    return (
                      <Stack gap={"none"} direction={"column"} key={index} className="al-space-y-1">
                        <span className="al-font-regular al-text-gray-900 al-truncate">
                          {t.teammate}
                        </span>
                        <div className="al-w-full al-bg-gray-200 al-rounded-full al-h-2">
                          <div
                            className="al-bg-purple-400 al-h-2 al-rounded-full al-transition-all"
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <span className="al-text-gray-600 al-font-normal">
                          {formatNumber(t.credits_consumed)} credits ({percentage.toFixed(1)}%)
                        </span>
                      </Stack>
                    );
                  })}
                </div>
              }
            >
              {content}
            </Tooltip>
          );
        }

        return content;
      },
      id: 2,
    },
    {
      label: "Last Used",
      key: "last_used_on",
      uid: "last_used_on",
      render: (item: TransactionGroupedByUser) => (
        <span>{dayjs(item.last_used_on).format("DD MMM YYYY, hh:mm A")}</span>
      ),
      id: 3,
    },
  ];

  const handleTabChange = (value: "task" | "users") => {
    setTab(value);
    setPage(0); // Reset to first page when tab changes
    setPageSize(10); // Reset to default page size when tab changes
  };

  const updatePageSize = (size: number) => {
    setPageSize(size);
    setPage(0); // Reset to first page when page size changes
  };

  const handleDownload = () => {
    if (tab === "task") {
      downloadOrgUsageCSV({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
    } else {
      downloadOrgUsageByUsersCSV({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <Stack align={"center"} justify={"between"}>
          <Typography variant={"section-heading"}>Credits Usage History</Typography>
          <Stack gap="sm" align="center">
            <Button variant="outline" onClick={handleDownload}>
              <DownloadIcon />
              Download CSV
            </Button>
            <DateRange
              startDate={startDate}
              endDate={endDate}
              onDateRangeSelect={(start, end) => {
                onUpdate(start, end);
              }}
            />
          </Stack>
        </Stack>
      </CardHeader>
      <CardContent>
        {/* @ts-ignore - Tabs component typing issue with conditional children */}
        <Tabs variant={"underlined"} defaultValue="task" onValueChange={handleTabChange}>
          <TabsList className={"al-mb-2"}>
            <TabsTrigger value={"task"}>Task</TabsTrigger>
            <TabsTrigger value={"users"}>Users</TabsTrigger>
          </TabsList>
          <TabsContent value={"task"}>
            {isLoadingTableData ? (
              <Stack justify={"center"} align={"center"} className={"al-min-h-80"} gap={"lg"}>
                <Spinner size={"lg"} />
                <span>Loading...</span>
              </Stack>
            ) : (
              <>
                <DataTable
                  header={taskTableHeaders}
                  onRowClick={() => {}}
                  data={{ items: tasksTableData?.items ?? [] }}
                  resetPage={() => {}}
                />
                <Paginate
                  itemCount={tasksTableData?.total || 0}
                  numPages={tasksTableData?.pages || 0}
                  page={page}
                  pageSize={pageSize}
                  onPageClick={setPage}
                  onPageSizeChange={updatePageSize}
                  showPageSize
                />
              </>
            )}
          </TabsContent>
          <TabsContent value={"users"}>
            {isLoadingGroupedByUserTableData ? (
              <Stack justify={"center"} align={"center"} className={"al-min-h-80"} gap={"lg"}>
                <Spinner size={"lg"} />
                <span>Loading...</span>
              </Stack>
            ) : (
              <>
                <DataTable
                  header={usersTableHeader}
                  onRowClick={() => {}}
                  data={{ items: transactionsGroupedByUsersTableData?.items ?? [] }}
                  resetPage={() => {}}
                />
                <Paginate
                  itemCount={transactionsGroupedByUsersTableData?.total || 0}
                  numPages={transactionsGroupedByUsersTableData?.pages || 0}
                  page={page}
                  pageSize={pageSize}
                  onPageClick={setPage}
                  onPageSizeChange={updatePageSize}
                  showPageSize
                />
              </>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
